import { Imports } from '@gleamer/types';
import React, { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  useImportBatch,
  useImportBatchFiles,
  useImportBatchLines,
} from '../../../../hooks/useImports';
import { PageButton } from '../../../atoms/PageButton';
import { PageTitle } from '../../../atoms/PageTitle';
import { Card, Field as LabelledControl, Pager as SharedPager, selectClassName } from '../../../atoms/Surface';
import { TableBodyTd, TableHeadTh } from '../../../atoms/Table';
import { TraceLink } from '../TraceLink';
import {
  fileBreakdown,
  formatBytes,
  formatDateTime,
  progressSegments,
} from './format';
import {
  FileStatusBadge,
  ImportStatusBadge,
  LineStatusBadge,
  lineStatusLabel,
} from './ImportStatusBadge';
import { ProgressBar } from './ProgressBar';

type ImportLineStatus = Imports.ImportLineStatus;
type ImportCounters = Imports.ImportCounters;

const { IMPORT_LINE_STATUSES, isFinalStatus } = Imports;

export const LINES_PAGE_SIZE = 50;
export const FILES_PAGE_SIZE = 100;

/** One figure of the batch's summary. */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0 rounded-2xl bg-dl-surface px-4 py-3 shadow-sm ring-1 ring-dl-border">
      <dt className="text-sm font-medium uppercase tracking-wide text-dl-muted">{label}</dt>
      <dd className="mt-1 break-words text-base text-dl-text">{children}</dd>
    </div>
  );
}

/** Previous / next, only when there is more than one page. */
function Pager({
  page,
  totalPages,
  total,
  noun,
  onPage,
}: {
  page: number;
  totalPages: number;
  total: number;
  noun: string;
  onPage: (page: number) => void;
}) {
  if (totalPages <= 1) {
    return null;
  }
  return <SharedPager page={page} pages={totalPages} total={total} noun={noun} onPage={onPage} />;
}

function LineCounters({ counters }: { counters: ImportCounters }) {
  const b = fileBreakdown(counters);
  return (
    <span className="whitespace-nowrap text-sm tabular-nums">
      {b.done.toLocaleString()} / {counters.files.toLocaleString()}
      {b.errors > 0 && <span className="ml-1 text-dl-danger">({b.errors} errors)</span>}
    </span>
  );
}

/** One line's files, or — with no line — the batch's files in error. */
function FilesTable({
  batchId,
  line,
  live,
}: {
  batchId: string;
  line?: string;
  live: boolean;
}) {
  const [page, setPage] = useState(0);
  const { data, status, error } = useImportBatchFiles(
    batchId,
    line
      ? { line, page, size: FILES_PAGE_SIZE }
      : {
          statuses: ['IMPORT_ERROR', 'EXPORT_ERROR'],
          page,
          size: FILES_PAGE_SIZE,
        },
    { live }
  );
  if (status === 'loading') {
    return <div>Loading…</div>;
  }
  if (status === 'error') {
    return (
      <div role="alert" className="text-dl-danger">
        Could not load the files: {(error as Error)?.message}
      </div>
    );
  }
  if (data.content.length === 0) {
    return <p>{line ? 'No file under this line yet.' : 'No file failed.'}</p>;
  }
  return (
    <>
      <table className="w-full table-auto">
        <thead>
          <tr>
            <TableHeadTh>File</TableHeadTh>
            <TableHeadTh>Status</TableHeadTh>
            <TableHeadTh>Size</TableHeadTh>
            <TableHeadTh>Importer</TableHeadTh>
            <TableHeadTh>Reason</TableHeadTh>
          </tr>
        </thead>
        <tbody>
          {data.content.map(file => (
            <tr key={`${file.path}#${file.generation}`} className="group">
              <TableBodyTd className="break-all font-mono text-sm">
                {file.path}
              </TableBodyTd>
              <TableBodyTd>
                <FileStatusBadge status={file.status} />
              </TableBodyTd>
              <TableBodyTd className="whitespace-nowrap text-sm">
                {formatBytes(file.size)}
              </TableBodyTd>
              <TableBodyTd className="font-mono text-sm">
                {file.importerVersion ?? '—'}
              </TableBodyTd>
              <TableBodyTd className="text-sm">{file.reason ?? ''}</TableBodyTd>
            </tr>
          ))}
        </tbody>
      </table>
      <Pager
        page={page}
        totalPages={data.totalPages}
        total={data.totalElements}
        noun="files"
        onPage={setPage}
      />
    </>
  );
}

function LinesSection({ batchId, live }: { batchId: string; live: boolean }) {
  const [statusFilter, setStatusFilter] = useState<ImportLineStatus | ''>('');
  const [page, setPage] = useState(0);
  const [openLine, setOpenLine] = useState<string | null>(null);
  const { data, status, error } = useImportBatchLines(
    batchId,
    {
      page,
      size: LINES_PAGE_SIZE,
      statuses: statusFilter ? [statusFilter] : undefined,
    },
    live
  );

  return (
    <Card
      id="lines-title"
      title="Lines"
      className="mb-6"
      description="One per CLIENT/PATIENT line of the list. Open one to see its files."
      actions={
        <LabelledControl label="Status">
          <select
            className={selectClassName}
            value={statusFilter}
            onChange={e => {
              setStatusFilter(e.target.value as ImportLineStatus | '');
              setPage(0);
            }}
          >
            <option value="">All</option>
            {IMPORT_LINE_STATUSES.map(s => (
              <option key={s} value={s}>
                {lineStatusLabel(s)}
              </option>
            ))}
          </select>
        </LabelledControl>
      }
    >
      {status === 'loading' && <div>Loading…</div>}
      {status === 'error' && (
        <div role="alert" className="text-dl-danger">
          Could not load the lines: {(error as Error)?.message}
        </div>
      )}
      {status === 'success' && data.content.length === 0 && <p>No line.</p>}
      {status === 'success' && data.content.length > 0 && (
        <>
          <table className="w-full table-auto">
            <thead>
              <tr>
                <TableHeadTh>Line</TableHeadTh>
                <TableHeadTh>Status</TableHeadTh>
                <TableHeadTh>Files done</TableHeadTh>
                <TableHeadTh>Size</TableHeadTh>
                <TableHeadTh>Patient</TableHeadTh>
                <TableHeadTh>Updated</TableHeadTh>
              </tr>
            </thead>
            <tbody>
              {data.content.map(line => (
                <React.Fragment key={line.prefix}>
                  <tr className="group">
                    <TableBodyTd className="break-all font-mono text-sm">
                      <button
                        type="button"
                        className="text-left text-dl-accent hover:underline"
                        aria-expanded={openLine === line.prefix}
                        onClick={() =>
                          setOpenLine(
                            openLine === line.prefix ? null : line.prefix
                          )
                        }
                      >
                        {line.prefix}
                      </button>
                      {line.lineNumbers.length > 1 && (
                        <span className="ml-1 text-dl-muted">
                          (lines {line.lineNumbers.join(', ')})
                        </span>
                      )}
                    </TableBodyTd>
                    <TableBodyTd>
                      <LineStatusBadge
                        status={line.status}
                        notFound={line.notFound}
                      />
                    </TableBodyTd>
                    <TableBodyTd>
                      <LineCounters counters={line.counters} />
                    </TableBodyTd>
                    <TableBodyTd className="whitespace-nowrap text-sm">
                      {formatBytes(line.bytes)}
                    </TableBodyTd>
                    <TableBodyTd className="font-mono text-sm">
                      {line.patient ?? '—'}
                    </TableBodyTd>
                    <TableBodyTd className="whitespace-nowrap text-sm">
                      {formatDateTime(line.updatedAt)}
                    </TableBodyTd>
                  </tr>
                  {openLine === line.prefix && (
                    <tr>
                      <td colSpan={6} className="bg-dl-sunken px-4 pb-4">
                        <FilesTable
                          batchId={batchId}
                          line={line.prefix}
                          live={live}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          <Pager
            page={page}
            totalPages={data.totalPages}
            total={data.totalElements}
            noun="lines"
            onPage={setPage}
          />
        </>
      )}
    </Card>
  );
}

export function ImportJobPage() {
  const { jobId } = useParams();
  const location = useLocation();
  const listSearch = (location.state as { listSearch?: string } | null)
    ?.listSearch;
  const {
    data: batch,
    status,
    error,
    refetch,
    isFetching,
  } = useImportBatch(jobId);

  const backLink = (
    <Link
      className="text-lg text-dl-accent hover:underline"
      to={{
        pathname: '/backoffice/imports',
        search: listSearch ? `?${listSearch}` : '',
      }}
    >
      ← All batches
    </Link>
  );

  if (status === 'loading') {
    return <div>Loading…</div>;
  }
  if (status === 'error') {
    return (
      <div>
        {backLink}
        <div role="alert" className="mt-2 text-dl-danger">
          Could not load import batch {jobId}: {(error as Error)?.message}
        </div>
      </div>
    );
  }

  const live = !isFinalStatus(batch.status);
  const b = fileBreakdown(batch.counters);
  const segments = progressSegments(batch.counters);

  return (
    <div>
      {backLink}
      <PageTitle
        className="mt-2 items-center"
        title={`Import batch #${batch.id}`}
      >
        <PageButton onClick={() => refetch()} disabled={isFetching} className="mx-0 h-10 px-5">
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </PageButton>
      </PageTitle>

      <dl className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Field label="Status">
          <ImportStatusBadge status={batch.status} progressAt={batch.progressAt} />
          <span className="ml-2 text-sm text-dl-muted">orchestrator: {batch.state}</span>
        </Field>
        <Field label="Name">{batch.name}</Field>
        <Field label="Source">
          <span className="break-all font-mono text-sm">gs://{batch.bucket}</span>
        </Field>
        <Field label="Requested by">{batch.requestedBy}</Field>
        <Field label="Requested">{formatDateTime(batch.requestedAt)}</Field>
        <Field label="Planned">{formatDateTime(batch.plannedAt)}</Field>
        <Field label="Last activity">{formatDateTime(batch.progressAt)}</Field>
        <Field label="Lines">
          {batch.lines.total} — {batch.lines.done} done, {batch.lines.onGoing} on going,{' '}
          {batch.lines.toDo} to do, {batch.lines.error} in error
          {batch.lines.notFound > 0 && ` (${batch.lines.notFound} not found)`}
        </Field>
        {(batch.traceId || batch.logsUrl) && (
          <Field label="Trace">
            <TraceLink traceId={batch.traceId} logsUrl={batch.logsUrl} />
          </Field>
        )}
      </dl>

      {batch.status === 'STALLED' && (
        <div
          role="status"
          className="mb-6 rounded-2xl bg-dl-warning-tint px-4 py-3 text-dl-warning ring-1 ring-dl-warning/40"
        >
          <span className="font-bold">Stalled: </span>
          nothing has moved since {formatDateTime(batch.progressAt)}. A file may
          be stuck or dead-lettered, or imports-assets may not be running.
        </div>
      )}

      {batch.error && (
        <div
          role="alert"
          className="mb-6 rounded-2xl bg-dl-danger-tint px-4 py-3 text-dl-danger ring-1 ring-dl-danger/50"
        >
          <span className="font-bold">The planning failed: </span>
          {batch.error}
        </div>
      )}

      <section
        aria-label="Progress"
        className="mb-6 rounded-2xl bg-dl-surface p-6 shadow-sm ring-1 ring-dl-border"
      >
        <h3 className="mb-3 text-xl font-semibold text-dl-text">
          Progress — {b.done.toLocaleString()} / {batch.counters.files.toLocaleString()} files done
        </h3>
        <ProgressBar label="Files by status" segments={segments} />
        <ul className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-base text-dl-muted">
          {segments.map(s => (
            <li key={s.label} className="flex items-center">
              <span className={`mr-2 inline-block h-2.5 w-2.5 rounded-full ${s.className}`} />
              {s.label}: <span className="ml-1 tabular-nums text-dl-text">{s.value.toLocaleString()}</span>
            </li>
          ))}
        </ul>
      </section>

      <LinesSection batchId={jobId} live={live} />

      <Card id="failed-files-title" title="Failed files">
        <FilesTable batchId={jobId} live={live} />
      </Card>
    </div>
  );
}

export default ImportJobPage;
